/* START GENAI@CHATGPT4 */
import { Component, Event, EventEmitter, Fragment, h, Prop, State, Watch } from '@stencil/core';
import { getQuestions, getQuizResult, postFinishQuiz, postStartQuiz } from '@src/components/vs-quiz/vs-quiz.helper';
import {
  Answer, ParsedQuizConfig,
  Question,
  QuestionOption, QuestionsResponse,
  QUIZ_DISPLAY_FORMAT,
  QUIZ_GET_QUESTION_SAMPLE, QuizInitPayLoad,
} from '@src/components/vs-quiz/vs-quiz.constant';
import { getDataFromPropString, updateOrRemoveFromList } from '@utils/utils';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';
import { redirectUrlSafe } from '@src/workflows/vs-standard-form/helpers/error.helper';


@Component({
  tag: 'vs-quiz',
  styleUrl: 'vs-quiz.css',
  shadow: false,
})
export class VsQuiz {

  /**
   * @shortDescription If true, enable the developer mode.
   * @controlName dev
   */
  @Prop() dev: string;

  /**
   @shortDescription quiz id, quiz label information
   @controlName quizConfig
   */
  @Prop() quizConfig: string;

  /**
   @shortDescription promo id of the promotion
   @controlName promoId
   */
  @Prop() promoId: string;

  /**
   @shortDescription promotion token
   @controlName token
   */
  @Prop() token: string;

  /**
   @shortDescription base url for the api
   @controlName baseUrl
   */
  @Prop() baseUrl: string;

  /**
   @shortDescription x data center
   @controlName xDataCenter
   */
  @Prop() xDataCenter: string;

  /**
   @shortDescription generic error message
   @controlName genericErrorMessage
   */
  @Prop() genericErrorMessage: string = 'An error occurred while submitting the quiz. Please try again.';


  /**
   * @shortDescription Emit form loading event
   * @controlName quizLoadingEvent */
  @Event({
    eventName: 'quizLoadingEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onQuizLoadingHandler: EventEmitter;

  @State() isLoading: boolean;

  @Watch('isLoading')
  onQuizLoadingChange(newValue: boolean) {
    this.onQuizLoadingHandler.emit(newValue);
  }

  /* Label definition */
  @State() quizId: string;
  @State() quizHeading: string;
  @State() quizDescription: string;
  @State() quizResultQnsCorrectLabel: string;
  @State() quizResultHeading: string;
  @State() quizResultTimeSpendLabel: string;
  @State() quizResultTotalScoreLabel: string;
  @State() quizSubmitButtonLabel: string;
  @State() quizNextButtonLabel: string;


  @State() isTakingQuiz: boolean = true;
  @State() quizDuration: string;
  @State() quizQuestions: any[];

  @State() currentQuestion: number = 0;
  @State() quizResultsStage: boolean;
  @State() quizType: string;
  @State() quizStartedToken: string;
  @State() quizResult: any;
  @State() responseResultMsg: string;
  @State() quizAnswers: Answer[] = [];
  @State() errorMessage: string = this.genericErrorMessage;
  // START GENAI@CHATGPT4
  @State() errorModalId: string;
  @State() devQuizType: string;

  // END GENAI@CHATGPT4

  componentWillLoad() {
    this.isLoading = true;
    const parsedQuizConfig: ParsedQuizConfig = getDataFromPropString(this.quizConfig);
    this.quizId = parsedQuizConfig?.id;
    this.devQuizType = parsedQuizConfig?.quizType;
    this.quizHeading = decodeHTMLEntities(parsedQuizConfig?.heading);
    this.quizDescription = decodeHTMLEntities(parsedQuizConfig?.description);
    this.quizResultQnsCorrectLabel = decodeHTMLEntities(parsedQuizConfig?.resultQnsCorrectLabel);
    this.quizResultHeading = decodeHTMLEntities(parsedQuizConfig?.resultHeading);
    this.quizResultTimeSpendLabel = decodeHTMLEntities(parsedQuizConfig?.resultTimeSpendLabel);
    this.quizResultTotalScoreLabel = decodeHTMLEntities(parsedQuizConfig?.resultTotalScoreLabel);
    this.quizSubmitButtonLabel = decodeHTMLEntities(parsedQuizConfig?.quizSubmitButtonLabel);
    this.quizNextButtonLabel = decodeHTMLEntities(parsedQuizConfig?.quizNextButtonLabel);
    this.initQuiz();
  }

  initQuiz = () => {
    // if dev mode, set default question, if not, get questions from api
    if (this.isDevMode()) {
      this.isTakingQuiz = true;
      this.isLoading = false;
      this.quizType = this.devQuizType || QUIZ_DISPLAY_FORMAT.ALL_TOGETHER;
      this.processQuestions(QUIZ_GET_QUESTION_SAMPLE.questions);
    } else {
      const getQuizQuestionTimeOut = setTimeout(() => {
        clearTimeout(getQuizQuestionTimeOut);
        this.getQuizQuestions();
      }, 10);
    }
  };

  // Step 1: Getting the Quiz Questions: get quiz questions, shuffle the answers if necessary, set the quiz duration and display format

  // START GENAI@CHATGPT4
  getInitQuizPayload(): QuizInitPayLoad {
    return {
      promoId: this.promoId,
      quizId: this.quizId,
    } as QuizInitPayLoad;
  }

  // END GENAI@CHATGPT4
  // START GENAI@CHATGPT4
  initQuizAnswersList() {
    this.quizAnswers = this.quizQuestions.map(question => ({
      key: question.id,
      values: [],
    }));
  }


// END GENAI@CHATGPT4

// START GENAI@CHATGPT4
  processQuestions = (questions: Question[]): void => {
    if (!questions) {
      this.handleQuizError();
    }
    this.quizQuestions = questions?.map((question: Question) => {
      if (question?.options?.length > 0) {
        const options: QuestionOption[] = [...question?.options];
        if (question?.shuffle) {
          options.sort(() => Math.random() - 0.5);
        } else {
          options.sort((a, b) => a.sort - b.sort);
        }
        return { ...question, options };
      } else {
        return { ...question };
      }
    });
  };
// END GENAI@CHATGPT4

  isDevMode = () => {
    return (this.dev === 'true');
  };

  getErrorModalDom = () => {
    return <vs-modal modal-id={this.errorModalId}
                     modal-title='Error'
                     modal-content={this.errorMessage}
                     trigger-ref='quizSubmitButton'
                     secondary-ref='quizResultHeading'
                     onModalCloseEvent={() => {
                       this.closeErrorModal();
                       redirectUrlSafe('/');
                     }} />;
  };

// START GENAI@CHATGPT4
  getQuizQuestions() {
    getQuestions(this.baseUrl, this.token, this.xDataCenter, this.getInitQuizPayload())
      .then((response: QuestionsResponse) => {
        if (response?.success === true) {
          this.processQuestions(response?.questions);
          this.initQuizAnswersList();
          if (response?.quizDuration) {
            this.quizDuration = response.quizDuration;
          }
          this.quizType = response?.displayFormat;
          this.startQuiz();
        } else {
          this.setErrorMessage(response?.message);
          this.showErrorModal();
        }
      }).catch(() => {
      this.handleQuizError();
    });
  }

  setErrorMessage(errorMessage: string) {
    this.errorMessage = errorMessage?.length > 0 ? errorMessage : this.genericErrorMessage;
  }

  handleQuizError() {
    this.isLoading = false;
    this.showErrorModal();
  }


  // START GENAI@CHATGPT4
  showErrorModal = (): void => {
    this.isLoading = false;
    this.errorModalId = `errorModal${Math.random()}`;
    const modalToggleTimeOut = setTimeout(() => {
      clearTimeout(modalToggleTimeOut);
      let openEvent = new CustomEvent('vs_modal_toggle', {
        detail: {
          id: this.errorModalId,
          open: true,
        },
      });
      document.dispatchEvent(openEvent);
    }, 200);
  };

  closeErrorModal() {
    let closeEvent
      = new CustomEvent('vs_modal_toggle', {
      detail: {
        id: this.errorModalId,
        open: false,
      },
    });
    document.dispatchEvent(closeEvent);
    this.errorModalId = null;
  }

  // END GENAI@CHATGPT4


  // Step 2: Starting the Quiz: API call to trigger start of quiz. If timed, start timer
  startQuiz() {
    this.isLoading = true;
    postStartQuiz(this.baseUrl, this.token, this.xDataCenter, this.getInitQuizPayload())
      .then(response => {
        this.isLoading = false;
        if (response?.success === true) {
          this.quizStartedToken = response.token;
        } else {
          if (response.message) {
            this.setErrorMessage(response?.message);
          }
        }
      }).catch(() => {
      this.handleQuizError();
    });
  }

  // Step 3: Answering a Question: one-at-a-time quiz handling, if last of the one-at-a-time quiz, submit the answer

  getQuizAnswers(answerObj: Answer) {
    if (answerObj?.key) {
      this.quizAnswers = updateOrRemoveFromList(this.quizAnswers, answerObj.key, answerObj);
    }
  }

  handleUpdatedAnswer = (e): void => {
    this.getQuizAnswers(e);
    if (this.quizType === QUIZ_DISPLAY_FORMAT.ONE_AT_A_TIME) {
      this.isLoading = true;
      const changeQuestionStateTimeOut = setTimeout(() => {
        clearTimeout(changeQuestionStateTimeOut);
        if (this.quizQuestions?.[this.currentQuestion + 1]) {
          this.currentQuestion++;
        }
        this.isLoading = false;
      }, 1);
    }
  };

  // Step 4: Submitting Answers: submit the answer with feedback on of won the quiz or disqualified

  handleQuizTimesUp = (isQuizTimeUp) => {
    if (isQuizTimeUp.detail) {
      this.submitAnswers();
    }
  };

  // START GENAI@CHATGPT4
  populateFinishQuizJson = () => {
    return {
      promoId: this.promoId,
      quizId: this.quizId,
      token: this.quizStartedToken,
      answer: [...this.quizAnswers],
    };
  };

  // END GENAI@CHATGPT4

  submitAnswers() {
    let data = this.populateFinishQuizJson();
    this.isLoading = true;

    postFinishQuiz(this.baseUrl, this.token, this.xDataCenter, data).then(response => {
      this.isLoading = false;
      if (response.success === true) {
        this.isLoading = true;
        this.quizAnswers = [];
        this.getResult();
      } else {
        this.setErrorMessage(response?.message);
        this.showErrorModal();
      }
    }).catch(() => {
      this.handleQuizError();
    });
  }

  // START GENAI@CHATGPT4

// END GENAI@CHATGPT4

  getResult() {
    this.isLoading = true;
    getQuizResult(this.baseUrl, this.token, this.xDataCenter, {
      promoId: this.promoId,
    }).then(response => {
      this.isLoading = false;
      if (response.success === true) {
        this.quizResult = response.result;
        this.isTakingQuiz = false;
        if (response?.message) {
          if (response?.redirect) {
            redirectUrlSafe(response.message);
          } else {
            this.responseResultMsg = response.message;
          }
        }
      } else {
        this.setErrorMessage(response?.message);
        this.showErrorModal();
      }
    }).catch(() => {
      this.handleQuizError();
    });
  }

  // Step 5: Handling Errors: If there are any errors in the responses, appropriate error messages are displayed and the user is redirected to the landing page.

  onQuizElementTimesUp = (event) => {
    console.log(event);
    this.submitAnswers();
  };

  onUpdateAnsweredQuestionEvent = (event) => {
    this.handleUpdatedAnswer(event.detail);
  };

  onSubmitAnsweredQuestionEvent = (event) => {
    this.getQuizAnswers(event.detail);
    this.submitAnswers();
  };

  getQuizRenderView() {
    return <Fragment>
      <div class='vs-container'>
        <h1 class='vs-h2 vs-mt-3'>{this.quizHeading}</h1>
        {/* Enable Quiz counter for timed quiz all tgt */}
        {
          this.quizDuration && this.quizType === QUIZ_DISPLAY_FORMAT.ALL_TOGETHER &&
          <vs-quiz-countdown quiz-time={this.quizDuration} onTimesUpEvent={this.handleQuizTimesUp} />
        }
        <hr class='divider-line' />
        <p class='vs-text'>{this.quizDescription}</p>
        {/* Quiz display for one question at a time */}
        {
          this.quizQuestions?.length > 0
          && this.quizType === QUIZ_DISPLAY_FORMAT.ONE_AT_A_TIME &&
          <Fragment>{
            !this.isLoading &&
            <vs-quiz-element question={JSON.stringify(this.quizQuestions[this.currentQuestion])}
                             quiz-type={this.quizType}
                             question-number={this.currentQuestion}
                             questions-total-num={this.quizQuestions.length}
                             quiz-submit-button-label={this.quizSubmitButtonLabel}
                             quiz-next-button-label={this.quizNextButtonLabel}
                             onUpdateAnsweredEvent={this.onUpdateAnsweredQuestionEvent}
                             onSubmitAnsweredEvent={this.onSubmitAnsweredQuestionEvent}>
            </vs-quiz-element>
          }</Fragment>
        }
        {/* Quiz display for multiple questions display in one form */}
        {
          this.quizQuestions?.length > 0
          && this.quizType === QUIZ_DISPLAY_FORMAT.ALL_TOGETHER &&
          this.quizQuestions.map((question, questionIndex: number) =>
            (<vs-quiz-element question={JSON.stringify(question)}
                              quiz-duration={question.quizDuration}
                              quiz-type={this.quizType}
                              question-number={questionIndex}
                              questions-total-num={this.quizQuestions.length}
                              quiz-submit-button-label={this.quizSubmitButtonLabel}
                              quiz-next-button-label={this.quizNextButtonLabel}
                              onUpdateAnsweredEvent={this.onUpdateAnsweredQuestionEvent}
                              onSubmitAnsweredEvent={this.onSubmitAnsweredQuestionEvent}>
            </vs-quiz-element>))
        }
      </div>
    </Fragment>;
  }

  getQuizResultRenderView() {
    return <Fragment>
      <div class='vs-container results'>
        <h1 id='quizResultHeading' class='vs-h2 vs-mt-3'>{this.quizResultHeading}</h1>
        <hr class='divider-line' />
        {
          this.quizResult?.maskedEmail &&
          <Fragment><h2 class='vs-lead'>{this.quizResult?.maskedEmail}</h2>
            <hr class='divider-line' />
          </Fragment>
        }
        <div class='vs-pt-3'>
          <div class='daily_result'>
            <p class='vs-h4 vs-mb-1'><strong>{this.quizResultQnsCorrectLabel}</strong></p>
            <p class='vs-text vs-mb-1'>{this.quizResult?.correctAns}</p>
            {
              this.quizResultTimeSpendLabel?.length > 0 &&
              <Fragment>
                <p class='vs-h4 vs-mb-1'><strong>{this.quizResultTimeSpendLabel}</strong></p>
                <p class='vs-text vs-mb-1'>{this.quizResult?.time}</p>
              </Fragment>
            }
            <p class='vs-h4 vs-mb-1'><strong>{this.quizResultTotalScoreLabel}</strong></p>
            <p class='vs-text vs-mb-1'>{this.quizResult?.score}</p>
            <hr class='vs-mt-3 divider-line' />
            <p class='vs-text'>{this.responseResultMsg}</p>
          </div>
        </div>
      </div>
    </Fragment>;
  }

  render() {
    return (
      <Fragment>
        {this.errorModalId && this.errorMessage && this.getErrorModalDom()}
        {
          this.isTakingQuiz ? this.getQuizRenderView() : this.getQuizResultRenderView()
        }
      </Fragment>
    );
  }
}

/* END GENAI@CHATGPT4 */
